import {
  cloneTextStyle,
  createInitialsFromName,
  getUserInitials,
  getUserProfileUriOrEmptyString,
  isEqual,
  lerpV2,
  remapNumber,
  remapNumber01,
  v2,
} from './helpers';
import {
  addGameObject,
  anchoredPos,
  getZeroEntity,
  getZeroStaticEntity,
  moveEntityToBack,
  moveEntityToFront,
  setEntityPosition,
  setParent,
} from './entity';
import { Alignment_Horizontal, ObjectType, type ClientAnswer } from './shared/api_shared_types';
import { mouseUpThisFrame, setVisibilityState } from './simulate';
import {
  VisibilityState,
  GameManager,
  GameObjectController,
  MoveMode,
  RenderItemType,
  Shape,
  Slider,
  SliderOrientation,
  TextAppearStyle,
  type SliderAltAnswerObject,
  type TextObject,
  type V2,
  TextAlignHor,
  type Color,
} from './types';

import { SHAPE_STYLES, TEXT_STYLE } from './constants';
import { COLORS } from './constants-colors';
import { createAndAddLocalTextButton } from './textbutton';
import { changeTextObjectText, createAndAddLocalTextObject } from './textobject/textobject';
import { dt } from '.';
import { createImageEnt } from './image_entity';
import * as DataController from './controllers/data_controller';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageItem,
  type RenderPackageRect,
  type RenderPackageText,
} from './render_package';
import logger from './controllers/logger';
import { createAndAddLocalContainer, setContainerStyles } from './container';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    createPackage,
  };
}

export function createAndAddSlider(
  manager: GameManager,
  applyLabel: string,
  orientation: SliderOrientation = SliderOrientation.HORIZONTAL,
  length: number = 500,
): Slider {
  let newSlider: Slider = {
    ...getZeroEntity(),
    orientation,
    objectType: ObjectType.SLIDER,
    min: 1,
    max: 10,
    startUnSet: false,
    valueSet: false,
    valuePercentage: -1,
    knobPos: v2(),
    knobSpeed: 1,
    knob: null,
    altAnswers: [],
    altAnswerObjects: [],
    valueInt: 0,
    continueButton: null,
    valueSetOnce: false,
    knobChanged: false,
    updateValue: false,
    locked: false,
    showContinueButton: false,
    requestSetNewValue: false,
    newValue: 0,
    knobFontSize: 32,
    valueSetByUserThisFrame: false,
    knobIsDragged: false,
    maxBarWidth: 10,
    barWidth: 10,

    secondValuePercentage: 0,
    barColorSecond: COLORS.BLUE_CRAYOLA,

    useThirdBar: false,
    thirdValuePercentage: 0,
    barColorThird: COLORS.BLUE_CRAYOLA,

    barColorBack: COLORS.BLUE_CRAYOLA,
    barColorValue: COLORS.BLUE_CRAYOLA,
    applyButtonLabel: applyLabel || 'apply',
    doShowInfo: false,
    tObjInfo: null,
    infoText: '',
    infoIsShown: false,
    snapToInt: false,
    answerObject: null,
    tooltip: '',
    tObjTooltip: null,
    labels: [],
    showCurrentLabel: false,
  };

  newSlider.shape = Shape.RECT;
  newSlider.width = orientation === SliderOrientation.HORIZONTAL ? length : 60;
  newSlider.height = orientation === SliderOrientation.HORIZONTAL ? 60 : length;
  newSlider.moveMode = MoveMode.STATIC;
  newSlider.influenceKinetic = true;
  newSlider.allowDrag = false;
  newSlider.visibilityState = VisibilityState.DEFAULT;
  newSlider.clickable = true;

  const knob = createAndAddLocalContainer(manager, getZeroStaticEntity());
  knob.shape = Shape.CIRCLE;
  knob.radius = 30;
  setContainerStyles(knob, SHAPE_STYLES.SLIDER.KNOB, SHAPE_STYLES.SLIDER.KNOB_HL);
  knob.moveMode = MoveMode.STATIC;
  knob.influenceKinetic = false;
  knob.allowDrag = false;
  knob.allowHighlight = true;
  knob.passHover = true;
  knob.passClick = true;
  knob.visibilityState = VisibilityState.DEFAULT;

  newSlider.knob = knob;

  newSlider.answerObject = {
    container: null,
    tObj: null,
    imageEnt: null,
    color: COLORS.BLUE_CRAYOLA,
    pos: v2(),
    tooltip: '',
  };

  addGameObject(manager, newSlider);

  setParent(newSlider.knob, newSlider);

  setVisibilityState(newSlider, VisibilityState.DEFAULT);

  return newSlider;
}

function update(slider: Slider): boolean {
  let knob = slider.knob;
  let isDirty = false;

  slider.valueSetByUserThisFrame = false;

  if (slider.knob.startHighlightThisFrame || slider.knob.stopHighlightThisFrame) {
    isDirty = true;
  }

  if (!slider.locked) {
    if (slider.mouseDownOnMe) {
      slider.knobIsDragged = true;
      let relativeCursorPos =
        slider.orientation === SliderOrientation.HORIZONTAL
          ? slider.relativeMousePos.x
          : slider.relativeMousePos.y;
      let length =
        slider.orientation === SliderOrientation.HORIZONTAL
          ? slider.width - 2 * knob.radius
          : slider.height - 2 * knob.radius;
      let halfLength = length / 2;
      if (relativeCursorPos < -halfLength) {
        relativeCursorPos = -halfLength;
      } else if (relativeCursorPos > halfLength) {
        relativeCursorPos = halfLength;
      }
      slider.knobPos =
        slider.orientation === SliderOrientation.HORIZONTAL
          ? v2(relativeCursorPos, 0)
          : v2(0, relativeCursorPos);
      slider.knobChanged = true;
      slider.updateValue = true;
    }

    if (slider.knobChanged && mouseUpThisFrame()) {
      slider.knobChanged = false;
      slider.valueSetByUserThisFrame = true;
      slider.updateValue = true;
      slider.knobIsDragged = false;

      if (slider.showContinueButton && !slider.continueButton) {
        const icon = 'buttonContinue';

        slider.continueButton = createAndAddLocalTextButton(
          slider.manager,
          getZeroEntity(),
          slider.applyButtonLabel,
          icon,
          TEXT_STYLE.BUTTON,
        );
        slider.continueButton.playSFX = true;
        setParent(slider.continueButton, slider.knob);
        slider.continueButton.pos =
          slider.orientation === SliderOrientation.HORIZONTAL ? v2(0, 60) : v2(120, 0);
        slider.continueButton.moveMode = MoveMode.STATIC;
        slider.continueButton.allowDrag = false;
        setVisibilityState(slider.continueButton, VisibilityState.APPEARING);
      }
    }

    if (slider.doShowInfo) {
      if (!slider.infoIsShown && (slider.startHighlightThisFrame || slider.knobChanged)) {
        showInfo(slider);
      } else if (slider.infoIsShown && !slider.knobChanged && !slider.highlighted) {
        hideInfo(slider);
      }
    }

    if (slider.updateValue) {
      let knobPosL =
        slider.orientation === SliderOrientation.HORIZONTAL ? slider.knobPos.x : slider.knobPos.y;

      let length =
        slider.orientation === SliderOrientation.HORIZONTAL ? slider.width : slider.height;

      let halfLength = length / 2 - knob.radius;

      let p = remapNumber01(knobPosL, -halfLength, halfLength, true);
      setSliderValue(slider, p, false);
      slider.updateValue = false;

      if (!slider.knobIsDragged && slider.snapToInt) {
        const snapToValue = slider.valueInt;
        const range = slider.max - slider.min;
        const snapToPercentage = (snapToValue - slider.min) / range;
        setSliderValue(slider, snapToPercentage, true);
      }
    }

    if (slider.isDirty) {
      updateSliderAfterValueChange(slider, true);
    }
  }

  if (!slider.knobIsDragged && slider.requestSetNewValue) {
    slider.requestSetNewValue = false;
    setSliderValue(slider, slider.newValue, true);
  }

  if (slider.highlighted) {
    let length = slider.orientation === SliderOrientation.HORIZONTAL ? slider.width : slider.height;
    let halfLength = length / 2;
    let p = remapNumber01(slider.relativeMousePos.x, -halfLength, halfLength, true);
    let scaled = p * slider.max;

    setSliderThirdScaledValue(slider, scaled);

    if (slider.barWidth !== slider.maxBarWidth) {
      const newWidth = slider.barWidth + dt * 40;
      slider.barWidth = Math.min(newWidth, slider.maxBarWidth);
      isDirty = true;
    }
    let toolTip = '';
    let posX = 0;
    if (slider.knob.highlighted) {
      toolTip = slider.tooltip;
      posX = slider.knob.pos.x;
      if (slider.answerObject.container) {
        moveEntityToFront(slider.answerObject.container);
      }
      for (let i = slider.altAnswerObjects.length - 1; i >= 0; i--) {
        const _posX = slider.altAnswerObjects[i].pos.x;
        const diffX = Math.abs(posX - _posX);
        if (diffX < 0.1) {
          toolTip += '\n' + slider.altAnswerObjects[i].tooltip;
        }
      }
    } else {
      for (let i = slider.altAnswerObjects.length - 1; i >= 0; i--) {
        const altAnswerObject = slider.altAnswerObjects[i];
        const _posX = altAnswerObject.pos.x;
        const diffX = Math.abs(slider.relativeMousePos.x - _posX);
        if (diffX < slider.knob.radius) {
          toolTip = altAnswerObject.tooltip;
          if (altAnswerObject.container) {
            moveEntityToFront(altAnswerObject.container);
          }
          posX = _posX;
          for (let j = slider.altAnswerObjects.length - 1; j >= 0; j--) {
            if (j === i) continue;
            const _posX = slider.altAnswerObjects[j].pos.x;
            const diffX = Math.abs(posX - _posX);
            if (diffX < 0.1) {
              toolTip += '\n' + slider.altAnswerObjects[j].tooltip;
            }
          }
        }
      }
    }
    if (toolTip.length) {
      showLabel(slider, toolTip, posX);
      moveEntityToFront(slider);
    } else if (slider.tObjTooltip) {
      setVisibilityState(slider.tObjTooltip, VisibilityState.DISAPPEARING);
      slider.tObjTooltip = null;
    }
  } else {
    if (slider.barWidth !== slider.maxBarWidth / 2) {
      const newWidth = slider.barWidth - dt * 40;
      slider.barWidth = Math.max(newWidth, slider.maxBarWidth / 2);
      isDirty = true;
      if (slider.tObjTooltip) {
        setVisibilityState(slider.tObjTooltip, VisibilityState.DISAPPEARING);
        slider.tObjTooltip = null;
      }
    }

    setSliderThirdScaledValue(slider, 0);
  }

  const pos = lerpV2(slider.knob.pos, slider.knobPos, slider.knobSpeed);
  if (!isEqual(pos, slider.knob.pos)) {
    setEntityPosition(slider.knob, pos.x, pos.y);
    isDirty = true;
  }

  return isDirty;
}

export function setSliderScaledValue(slider: Slider, scaledValue: number, updateKnob: boolean) {
  if (updateKnob && slider.knobIsDragged) return; //now the user is in control

  scaledValue = Math.max(scaledValue, slider.min);
  scaledValue = Math.min(scaledValue, slider.max);

  const valuePercentage = (scaledValue - slider.min) / (slider.max - slider.min);

  if (slider.valuePercentage !== valuePercentage) {
    slider.valuePercentage = valuePercentage;
    slider.valueInt = Math.floor(scaledValue + 0.5);
    updateSliderAfterValueChange(slider, updateKnob);

    slider.isDirty = true;

    slider.valueSet = true;
  }
}

export function setSliderValue(slider: Slider, value: number, updateKnob: boolean) {
  if (updateKnob && slider.knobIsDragged) return; //now the user is in control

  slider.valuePercentage = value;
  const scaledValue = slider.min + (slider.max - slider.min) * value;
  slider.valueInt = Math.floor(scaledValue + 0.5);

  slider.valueSet = true;

  updateSliderAfterValueChange(slider, updateKnob);
}

export function setSliderSecondaryScaledValue(slider: Slider, scaledValue: number) {
  const secondaryValuePercentage = (scaledValue - slider.min) / (slider.max - slider.min);

  if (slider.secondValuePercentage !== secondaryValuePercentage) {
    slider.secondValuePercentage = secondaryValuePercentage;

    slider.isDirty = true;
  }
}

export function setSliderThirdScaledValue(slider: Slider, thirdValue: number) {
  const thirdValuePercentage = (thirdValue - slider.min) / (slider.max - slider.min);

  if (slider.thirdValuePercentage !== thirdValuePercentage) {
    slider.thirdValuePercentage = thirdValuePercentage;

    slider.isDirty = true;
  }
}

function updateSliderAfterValueChange(slider: Slider, updateKnob: boolean) {
  if (slider.tObjInfo) {
    changeTextObjectText(slider.tObjInfo, slider.infoText);
  }

  if (updateKnob) {
    let length =
      slider.orientation === SliderOrientation.HORIZONTAL
        ? slider.width - 2 * slider.knob.radius
        : slider.height - 2 * slider.knob.radius;
    let halfLength = length / 2;

    let relativeKnobPosL = -halfLength + slider.valuePercentage * length;
    slider.knobPos =
      slider.orientation === SliderOrientation.HORIZONTAL
        ? v2(relativeKnobPosL, 0)
        : v2(0, relativeKnobPosL);
  }
}

function showInfo(slider: Slider) {
  if (!slider.tObjInfo) {
    const tObj = createAndAddLocalTextObject(
      slider.manager,
      getZeroStaticEntity(),
      slider.infoText,
      TEXT_STYLE.ZOOM_INFO,
      1,
      false,
    );
    tObj.onlyShowInDefaultState = false;
    tObj.appearStyle = TextAppearStyle.FADE_AND_MOVE_LEFT;
    slider.tObjInfo = tObj;
    setParent(slider.tObjInfo, slider.knob, v2(-60, 0));
  }

  setVisibilityState(slider.tObjInfo, VisibilityState.APPEARING);

  slider.infoIsShown = true;
}

function hideInfo(slider: Slider) {
  if (slider.tObjInfo) {
    setVisibilityState(slider.tObjInfo, VisibilityState.HIDING);
  }

  slider.infoIsShown = false;
}

function getaltAnswerColorByIndex(index: number): Color {
  index = Math.max(index, 0);

  const altAnswerColors = [
    COLORS.BLUE_CRAYOLA,
    COLORS.RED_SALSA,
    COLORS.GREEN_100,
    COLORS.DARKGREY1_100,
  ];

  return altAnswerColors[index % altAnswerColors.length];
}

export function setSliderAltValues(slider: Slider, answers: ClientAnswer[]) {
  slider.altAnswers = answers;

  //clear data
  for (let i = 0; i < slider.altAnswerObjects.length; i++) {
    if (slider.altAnswerObjects[i].imageEnt) {
      setVisibilityState(slider.altAnswerObjects[i].imageEnt, VisibilityState.DISAPPEARING);
    }
  }

  slider.altAnswerObjects = [];

  const yOffsets = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  let xOffsets = [0, -25, 25, -50, 50, -75, 75, -100, 100];
  let tooltip = '';

  //generate new data
  for (let i = 0; i < slider.altAnswers.length; i++) {
    let offsetIndex = 0;
    let pos = v2();
    let length =
      slider.orientation === SliderOrientation.HORIZONTAL
        ? slider.width - 2 * slider.knob.radius
        : slider.height - 2 * slider.knob.radius;
    let halfLength = length / 2;

    const valuePercentage = (answers[i].value - slider.min) / (slider.max - slider.min);

    if (valuePercentage > 0.9) {
      xOffsets = [0, -25, -50, -75, -100];
    } else if (valuePercentage < 0.1) {
      xOffsets = [0, 25, 50, 75, 100];
    }

    const range = slider.max - slider.min;

    const compareWith = slider.valueInt;
    const diff = Math.abs((answers[i].value - compareWith) / range);
    const valuesAreSimilar = diff < 0.05;
    if (valuesAreSimilar) {
      offsetIndex = 1;
    }

    for (let j = 0; j < i; j++) {
      const compareWith = answers[j].value;
      const diff = Math.abs((answers[i].value - compareWith) / range);
      const valuesAreSimilar = diff < 0.05;
      if (valuesAreSimilar) {
        offsetIndex += 1;
      }
    }

    if (offsetIndex < 0 && !slider.snapToInt) offsetIndex = 0;

    let relativeKnobPosL = -halfLength + valuePercentage * length + xOffsets[offsetIndex];
    pos =
      slider.orientation === SliderOrientation.HORIZONTAL
        ? v2(relativeKnobPosL, yOffsets[offsetIndex])
        : v2(yOffsets[offsetIndex], relativeKnobPosL);

    const c = createAndAddLocalContainer(slider.manager, getZeroStaticEntity());
    c.shape = Shape.CIRCLE;
    c.radius = slider.knob.radius * 1.2;
    c.shapeStyle = { ...SHAPE_STYLES.NONE, fillColor: getaltAnswerColorByIndex(i + 1) };
    setParent(c, slider, pos);
    moveEntityToBack(c);

    let imageEnt = null;
    let tObj = null;
    let uri = answers[i].img_uri;
    if (uri.length) {
      DataController.cacheImageFromApiAsync(slider.manager.user.accessToken, uri, uri);
      imageEnt = createImageEnt(slider.manager, getZeroStaticEntity(), uri);

      imageEnt.radius = slider.knob.radius * 1.2;
      imageEnt.shape = Shape.CIRCLE;
      imageEnt.strokeWeight = 1;
      imageEnt.strokeColor = COLORS.WHITE_100;
      setParent(imageEnt, c, v2());
      moveEntityToBack(imageEnt);
    } else {
      const name = answers[i].name;
      const initials = createInitialsFromName(name);
      tObj = createAnswerTObj(slider.manager, initials);

      setParent(tObj, c, v2());
    }

    let tooltip = answers[i].name;
    const altAnswerObject: SliderAltAnswerObject = {
      container: c,
      tObj,
      imageEnt,
      color: COLORS.BLUE_CRAYOLA,
      pos,
      tooltip,
    };

    slider.altAnswerObjects.push(altAnswerObject);
  }

  slider.knob.radius = 15 * 1.2;
  slider.answerObject.container = slider.knob;

  const profile_uri = getUserProfileUriOrEmptyString(slider.manager.user, 'thumb');
  if (profile_uri) {
    DataController.cacheImageFromApiAsync(
      slider.manager.user.accessToken,
      profile_uri,
      profile_uri,
    );
    const imageEnt = createImageEnt(slider.manager, getZeroStaticEntity(), profile_uri);
    // imageEnt.width = slider.knob.radius * 3;
    // imageEnt.height = slider.knob.radius * 3;
    imageEnt.radius = slider.knob.radius;
    imageEnt.strokeWeight = 1;
    imageEnt.strokeColor = COLORS.WHITE_100;
    imageEnt.shape = Shape.CIRCLE;
    setParent(imageEnt, slider.knob, v2());
    slider.answerObject.imageEnt = imageEnt;
  } else {
    const initials = getUserInitials(slider.manager.user);
    slider.answerObject.tObj = createAnswerTObj(slider.manager, initials);

    setParent(slider.answerObject.tObj, slider.knob, v2());
  }

  slider.tooltip = 'you';
}

function showLabel(slider: Slider, text: string, posX: number) {
  if (text.length) {
    if (!slider.tObjTooltip) {
      let style = cloneTextStyle(TEXT_STYLE.ICON_BUTTON_LABEL);

      style.textAlign = Alignment_Horizontal.CENTER;
      const appearStyle = TextAppearStyle.FADE_AND_MOVE_UP;

      slider.tObjTooltip = createAndAddLocalTextObject(
        slider.manager,
        getZeroStaticEntity(),
        text,
        style,
        2,
        false,
        0,
        1,
        appearStyle,
      );
      slider.tObjTooltip.onlyShowInDefaultState = false;
      slider.tObjTooltip.moveMode = MoveMode.ANCHORED;

      slider.tObjTooltip.anchoredPos = anchoredPos(v2(0.5, 0), v2(posX, 5), v2(0.5, 1));

      setParent(slider.tObjTooltip, slider);
    } else {
      changeTextObjectText(slider.tObjTooltip, text, true);
      slider.tObjTooltip.anchoredPos.offset.x = posX;
    }
  }
}

function createPackage(slider: Slider, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const height = slider.height;
  const width = slider.width;
  let length = SliderOrientation.HORIZONTAL ? width : height;
  if (slider.snapToInt) {
    length -= slider.knob.radius * 2;
  }

  {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      scale,
      width: slider.orientation === SliderOrientation.HORIZONTAL ? length : slider.maxBarWidth,
      height: slider.orientation === SliderOrientation.HORIZONTAL ? slider.barWidth : length,
      shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: slider.barColorBack },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  if (slider.secondValuePercentage > 0) {
    const scaledLength = length * slider.secondValuePercentage;
    let positionX = 0;
    let positionY = position.y;
    let width = 0;
    let height = 0;

    if (slider.orientation === SliderOrientation.HORIZONTAL) {
      positionX = position.x + scale * (-length / 2 + scaledLength / 2);

      width = scaledLength;
      height = slider.barWidth;
    } else {
      positionX = position.x + scale * (-length / 2 + scaledLength / 2);
      width = slider.maxBarWidth;
      height = scaledLength;
    }

    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX,
      positionY,
      width,
      height,
      shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: slider.barColorSecond },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  if (slider.useThirdBar && slider.thirdValuePercentage > 0) {
    const scaledLength = length * slider.thirdValuePercentage;
    let positionX = 0;
    let positionY = position.y;
    let width = 0;
    let height = 0;

    if (slider.orientation === SliderOrientation.HORIZONTAL) {
      positionX = position.x + scale * (-length / 2 + scaledLength / 2);

      width = scaledLength;
      height = slider.barWidth;
    } else {
      positionX = position.x + scale * (-length / 2 + scaledLength / 2);
      width = slider.maxBarWidth;
      height = scaledLength;
    }

    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX,
      positionY,
      width,
      height,
      shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: slider.barColorThird },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  if (!slider.snapToInt && slider.valuePercentage > 0) {
    const scaledLength = length * slider.valuePercentage;

    let positionX = 0;
    let positionY = position.y;
    let width = 0;
    let height = 0;

    if (slider.orientation === SliderOrientation.HORIZONTAL) {
      positionX = position.x + scale * (-length / 2 + scaledLength / 2);

      width = scaledLength;
      height = slider.barWidth;
    } else {
      positionX = position.x + scale * (-length / 2 + scaledLength / 2);
      width = slider.maxBarWidth;
      height = scaledLength;
    }

    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX,
      positionY,
      width,
      height,
      shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: slider.barColorValue },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };
    rPackage.push(rect);
  }

  if (slider.snapToInt) {
    let radius = slider.maxBarWidth * 2;
    const rangeWidth = length - radius - radius;
    const startX = -rangeWidth / 2;
    const parts = slider.max - slider.min;
    const partLength = rangeWidth / parts;
    for (let i = 0; i <= parts; i++) {
      const x = startX + i * partLength;
      {
        let circle: RenderPackageCircle = {
          type: RenderItemType.circle,
          ...EMPTY_RENDER_TRANSFORM,
          scale,
          positionX: position.x + scale * x,
          positionY: position.y,
          radius: radius,
          shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: COLORS.WHITE_100 },
          shadow: null,
        };
        rPackage.push(circle);
      }
      if (i < slider.labels.length) {
        if (!slider.showCurrentLabel || i + slider.min === slider.valueInt) {
          const maxValue = slider.showCurrentLabel ? partLength / 2 : partLength;
          const maxOpacity = slider.showCurrentLabel ? 1 : 0.5;
          const opacity =
            1 -
            remapNumber(
              Math.abs(slider.knobPos.x - x),
              partLength / 10,
              maxValue,
              0,
              maxOpacity,
              true,
            );

          const FONT_SIZE = 16;
          let rText: RenderPackageText = {
            type: RenderItemType.text,
            ...EMPTY_RENDER_TRANSFORM,
            scale,
            positionX: position.x + scale * x,
            positionY: position.y + scale * (slider.maxBarWidth * 12 + FONT_SIZE),
            positionLocalY: -FONT_SIZE / 2,
            hAlign: TextAlignHor.CENTER,
            fillColor: COLORS.WHITE_100,
            size: FONT_SIZE,
            text: slider.labels[i],
            fontId: 'font_regular',
            strokeWeight: 0,
            strokeColor: [0, 0, 0],
            shadow: null,
            opacity,
          };
          rPackage.push(rText);
        }
      }
    }
  }

  return rPackage;
}

function createAnswerTObj(manager: GameManager, label: string): TextObject {
  const t = createAndAddLocalTextObject(
    manager,
    getZeroStaticEntity(),
    label,
    TEXT_STYLE.CONTENT_DEFAULT_SMALL,
  );

  return t;
}
